// Κοινό sticky header των marketing σελίδων (MarketingHome, Buzz landing κ.λπ.).
// Tailwind utilities: απαιτεί το /marketing.css φορτωμένο (useStyleSheet) και
// wrapper με id="marketing-root" (το important scope του tailwind.marketing.config.js).
// props: t(key)->string με κλειδιά nav.features/how/pricing/faq/signin/contact,
// lang ('el'|'en'), setLanguage(code), anchors=true για τα in-page links.
import React, { useEffect, useState } from "react";
import BrandMark from "./BrandMark";

export const CONTACT_EMAIL = "info@estiasync.com";
export const CONTACT_PHONE_DISPLAY = "2294 701 845";
export const CONTACT_PHONE_TEL = "+302294701845";

const FLAG_EL_SRC = "/locales/el/flag.png";
const FLAG_EN_SRC = "/locales/en/flag.png";

function Container({ children, className = "", ...props }) {
  return (
    <div {...props} className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
}

export default function MarketingTopNav({ t, lang, setLanguage, anchors = true }) {
  const [scrolled, setScrolled] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const navTo = (href) => (e) => {
    setOpen(false);
    setContactOpen(false);

    if (!href.startsWith("#")) return;

    e.preventDefault();
    const id = href.slice(1);

    // iOS Safari: scrollIntoView can be flaky; window.scrollTo is more reliable
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;

      const headerOffset = 88; // adjust if your header height differs
      const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    });
  };

  useEffect(() => {
    const onDown = (e) => {
      if (!e.target.closest("[data-contact-menu]")) setContactOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const base = "transition-all duration-300 backdrop-blur supports-[backdrop-filter]:bg-white/60 bg-white/80";
  const elevated = scrolled ? "shadow-lg border-b border-slate-200" : "border-b border-transparent";

  const FlagBtn = ({ code, label, src }) => {
    const selected = lang === code;

    return (
      <button
        type="button"
        onClick={() => setLanguage(code)}
        aria-label={label}
        title={label}
        aria-pressed={selected}
        className={[
          "inline-flex items-center justify-center",
          "rounded-lg border p-1.5",
          "transition-all",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2",
          selected
            ? "bg-sky-50 border-sky-200 ring-1 ring-sky-200/70 shadow-sm"
            : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300",
        ].join(" ")}
      >
        <img
          src={src}
          alt={label}
          loading="lazy"
          decoding="async"
          className="h-4 w-6 rounded-[4px] object-cover"
        />
      </button>
    );
  };

  return (
    <header className={`sticky top-0 z-50 ${base} ${elevated}`}>
      <Container className="flex h-16 items-center justify-between">
        <a href="/" className="group flex items-center gap-2" aria-label="estiasync home">
          <BrandMark
            href="/"
            size={32}
            className="group-hover:opacity-95"
          />
        </a>
        <nav className="hidden md:flex items-center gap-4 lg:gap-8 text-sm" style={{width: 'calc(100% - 300px)'}}>
          {anchors && (
            <>
              <a href="#how" onClick={navTo("#how")} className="text-slate-600 hover:text-slate-900 transition-colors">{t("nav.how")}</a>
              <a href="#features" onClick={navTo("#features")} className="text-slate-600 hover:text-slate-900 transition-colors">{t("nav.features")}</a>
              <a href="#pricing" onClick={navTo("#pricing")} className="text-slate-600 hover:text-slate-900 transition-colors">{t("nav.pricing")}</a>
              <a href="#faq" onClick={navTo("#faq")} className="text-slate-600 hover:text-slate-900 transition-colors">{t("nav.faq")}</a>
            </>
          )}
          <a href="/login" className="text-slate-600 hover:text-slate-900">{t("nav.signin")}</a>

          <div className="relative" data-contact-menu>
            <button
              type="button"
              onClick={() => setContactOpen((v) => !v)}
              className="group inline-flex items-center rounded-xl border border-slate-300 px-4 py-2 font-medium hover:border-slate-400 bg-white"
            >
              <span className="mr-2">{t("nav.contact")}</span>
              <span className={`transition-transform ${contactOpen ? "rotate-180" : ""}`}>▾</span>
            </button>

            {contactOpen && (
              <div className="absolute right-0 mt-2 w-72 overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-slate-200">
                <a
                  href={`tel:${CONTACT_PHONE_TEL}`}
                  className="block px-4 py-3 hover:bg-slate-50"
                >
                  <div className="text-sm font-semibold text-slate-900">📞 {CONTACT_PHONE_DISPLAY}</div>
                  <div className="text-xs text-slate-500">{lang === "el" ? "Κάλεσέ μας" : "Call us"}</div>
                </a>

                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("estiasync – contact")}`}
                  className="block px-4 py-3 hover:bg-slate-50 border-t border-slate-100"
                >
                  <div className="text-sm font-semibold text-slate-900">✉️ {CONTACT_EMAIL}</div>
                  <div className="text-xs text-slate-500">{lang === "el" ? "Στείλε email" : "Send an email"}</div>
                </a>
              </div>
            )}
          </div>

          {/* Language Switcher */}
          <div className="ml-2 flex items-center gap-2">
            <FlagBtn code="el" label="Ελληνικά" src={FLAG_EL_SRC} />
            <FlagBtn code="en" label="English" src={FLAG_EN_SRC} />
          </div>
        </nav>
        <div className="md:hidden flex items-center gap-2">
          <div className="flex items-center gap-1">
            <FlagBtn code="el" label="Ελληνικά" src={FLAG_EL_SRC} />
            <FlagBtn code="en" label="English" src={FLAG_EN_SRC} />
          </div>
          <button
            className="inline-flex items-center rounded-lg border px-3 py-2 text-sm"
            onClick={() => setOpen(!open)}
            aria-label="Open menu"
          >
            ☰
          </button>
        </div>
      </Container>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <Container className="py-2 grid gap-2 text-sm">
            {anchors && (
              <>
                <a href="#how" className="py-2" onClick={navTo("#how")}>{t("nav.how")}</a>
                <a href="#features" className="py-2" onClick={navTo("#features")}>{t("nav.features")}</a>
                <a href="#pricing" className="py-2" onClick={navTo("#pricing")}>{t("nav.pricing")}</a>
                <a href="#faq" className="py-2" onClick={navTo("#faq")}>{t("nav.faq")}</a>
              </>
            )}
            <a href="/login" className="py-2">{t("nav.signin")}</a>
            <div className="mt-2 border-t border-slate-200 pt-2">
              <a className="py-2 flex items-center gap-2" href={`tel:${CONTACT_PHONE_TEL}`}>
                <span>📞</span> <span>{CONTACT_PHONE_DISPLAY}</span>
              </a>
              <a className="py-2 flex items-center gap-2" href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("estiasync – contact")}`}>
                <span>✉️</span> <span>{CONTACT_EMAIL}</span>
              </a>
            </div>
          </Container>
        </div>
      )}
    </header>
  );
}
